import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { gameAPI } from '../../services/api';
import './SubscriptionModal.css';

interface SubscriptionStatus {
  channel: boolean;
  subscribed: boolean;
  channels?: {
    channel: { id: string; name: { ru: string; en: string }; url: string };
  };
}

interface SubscriptionModalProps {
  subscriptionStatus: SubscriptionStatus;
  onSubscribed: () => void;
}

/**
 * Модальное окно проверки подписки на обязательные каналы
 * Блокирует использование приложения пока пользователь не подпишется
 */
const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  subscriptionStatus,
  onSubscribed
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [localStatus, setLocalStatus] = useState(subscriptionStatus);
  const [error, setError] = useState<string | null>(null);

  const isRu = i18n.language === 'ru';

  const channels = localStatus.channels || {
    channel: {
      id: '@safepermoon',
      name: { ru: 'Канал SafePerMoon', en: 'SafePerMoon Channel' },
      url: 'https://t.me/safepermoon'
    }
  };

  // Открыть канал/чат в Telegram
  const openChannel = (url: string) => {
    // Пробуем открыть через Telegram API если доступен
    const tg = window.Telegram?.WebApp as any;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(url);
    } else {
      window.open(url, '_blank');
    }
  };

  // Проверить подписку
  const checkSubscription = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await gameAPI.checkSubscription();
      const newStatus = response.data.data;

      setLocalStatus(newStatus);

      if (newStatus.subscribed) {
        // Подписан на все - закрываем модалку
        onSubscribed();
      } else {
        // Не подписан на канал
        setError(
          isRu
            ? 'Вы не подписаны на канал'
            : 'You are not subscribed to the channel'
        );
      }
    } catch (err) {
      console.error('Ошибка проверки подписки:', err);
      setError(isRu ? 'Ошибка проверки. Попробуйте позже.' : 'Check failed. Try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const modalContent = (
    <div className="subscription-modal-overlay">
      <div className="subscription-modal">
        {/* Заголовок */}
        <div className="subscription-modal-header">
          <div className="subscription-modal-icon">🔔</div>
          <h2 className="subscription-modal-title">
            {isRu ? 'Подписка на каналы' : 'Channel Subscription'}
          </h2>
        </div>

        {/* Контент */}
        <div className="subscription-modal-content">
          <p className="subscription-modal-description">
            {isRu
              ? 'Для игры необходимо подписаться на официальные каналы проекта:'
              : 'To play, you need to subscribe to the official project channels:'
            }
          </p>

          {/* Список каналов */}
          <div className="subscription-channels">
            {/* Канал */}
            <div className="subscription-channel-item">
              <div className="channel-info">
                <span className="channel-icon">📢</span>
                <span className="channel-name">
                  {isRu ? channels.channel.name.ru : channels.channel.name.en}
                </span>
                <span className={`channel-status ${localStatus.channel ? 'subscribed' : 'not-subscribed'}`}>
                  {localStatus.channel ? '✅' : '❌'}
                </span>
              </div>
              <button
                className="channel-subscribe-btn"
                onClick={() => openChannel(channels.channel.url)}
              >
                {localStatus.channel
                  ? (isRu ? 'Открыть' : 'Open')
                  : (isRu ? 'Подписаться' : 'Subscribe')
                }
              </button>
            </div>
          </div>

          {/* Ошибка */}
          {error && (
            <div className="subscription-error">
              {error}
            </div>
          )}
        </div>

        {/* Кнопка проверки */}
        <div className="subscription-modal-actions">
          <button
            className="subscription-check-btn"
            onClick={checkSubscription}
            disabled={isLoading}
          >
            {isLoading
              ? (isRu ? 'Проверяем...' : 'Checking...')
              : (isRu ? '✅ Проверить подписку' : '✅ Check Subscription')
            }
          </button>
        </div>
      </div>
    </div>
  );

  // Рендерим через портал чтобы быть поверх всего
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default SubscriptionModal;
